import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { ApiResponse } from '../models/api-response';
import { AddReportRequest, Report, ReportStatus } from '../models/report';
import { ReportType } from '../models/report-type';
import { Client } from '../models/client';
import { map, Observable, Subject } from 'rxjs';
import { ReportSectionData } from '../models/report-sections';
import { ReportStatusRequest } from '../models/report-status-request';
import { CommentRequest } from '../models/comment-request';
import { NewSectionRequest } from '../models/new-section-request';
import { SectionValidationRequest } from '../models/section-validation-request';
import { SnackbarService } from './snackbar.service';
import { tap } from 'rxjs/operators';
import { States } from '../models/states';
import { API_ENDPOINTS, buildEndpoint } from '../api-endpoint';
import { Locations } from '../models/Locations';
import { GetProjectFilesRequest } from '../models/get-project-files-request';
import { RuntimeConfigService } from '../config/runtime-config.service';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private snackbar = inject(SnackbarService);
  private recentReportsSignal = signal<Report[]>([]);
  private reportTypesSignal = signal<ReportType[]>([]);
  private clientsSignal = signal<Client[]>([]);
  private stateSignal = signal<States[]>([]);
  private locationSignal = signal<Locations[]>([]);
  private baseApiUrl: string;
  private apiVersion: string;

  constructor(
    private runtimeConfigService: RuntimeConfigService,
    private http: HttpClient  ) {
    this.baseApiUrl = this.runtimeConfigService.apiUrl;
    this.apiVersion = this.runtimeConfigService.apiVersion;
    this.loadReportTypes();
    this.loadClients();
    this.loadStates();
    this.loadLocations();
  }

  private refreshReportsSource = new Subject<void>();
  refreshChartData$ = this.refreshReportsSource.asObservable();

  getApiUrl(apiEndpoint: string): string {
    return buildEndpoint(this.baseApiUrl, this.apiVersion, 'reportservice', apiEndpoint);
  }

  getRecentReports(): Signal<Report[]> {
    return computed(() => this.recentReportsSignal());
  }

  loadReports(): void {
    this.refreshReportsSource.next();
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.RecentReports);
    this.http
      .get<ApiResponse<Report[]>>(apiUrl)
      .pipe(
        tap((response: ApiResponse<Report[]>) => {
          if (!response.success) {
            this.snackbar.showCustom({
              type: 'error',
              title: 'Error',
              message: response.message,
            });
          }
        }),
        map((response: ApiResponse<Report[]>) => response.data)
      )
      .subscribe({
        next: (reports) => this.recentReportsSignal.set(reports),
        error: (err) => {
         
        },
      });
  }

  getStatusDisplay(status: ReportStatus): string {
    return ReportStatus[status];
  }

  deleteReport(reportId: number): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.DeleteReport).replace(
      '{reportId}',
      reportId.toString()
    );
    return this.http.delete<ApiResponse<boolean>>(apiUrl);
  }

  findReportsByType(reportTypeId: number, projectId: number, clientId: number) {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ReportsByType);
    return this.http
      .post<ApiResponse<Report[]>>(apiUrl, {
        reportTypeId,
        projectId,
        clientId,
      })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      );
  }

  findProjectFiles(obj: GetProjectFilesRequest) {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetProjectFiles);
    return this.http.post<ApiResponse<Report[]>>(apiUrl, obj);
  }

  getReportTypes(): Signal<ReportType[]> {
    return computed(() => this.reportTypesSignal());
  }

  getClients(): Signal<Client[]> {
    return computed(() => this.clientsSignal());
  }

  getStates(): Signal<States[]> {
    return computed(() => this.stateSignal());
  }

  getLocations(): Signal<Locations[]> {
    return computed(() => this.locationSignal());
  }
  private loadReportTypes(): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ReportType);
    this.http
      .get<ApiResponse<ReportType[]>>(apiUrl)
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (types) => this.reportTypesSignal.set(types),
        error: (error) => console.error('Error loading report types:', error),
      });
  }

  private loadClients(): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.Clients);
    this.http
      .get<ApiResponse<Client[]>>(apiUrl)
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (clients) => this.clientsSignal.set(clients),
        error: (error) => console.error('Error loading clients:', error),
      });
  }

  private loadStates(): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.States);
    this.http
      .get<ApiResponse<States[]>>(apiUrl)
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (states) => this.stateSignal.set(states),
        error: (error) => console.error('Error loading states:', error),
      });
  }

  private loadLocations(): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.Locations);
    this.http
      .get<ApiResponse<Locations[]>>(apiUrl)
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (location) => this.locationSignal.set(location),
        error: (error) => console.error('Error loading states:', error),
      });
  }

  sendReportForApproval(request: ReportStatusRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ApprovalStatus);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  updateCommentSection(request: CommentRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateComments);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  getReportCount(): Observable<ApiResponse<number[]>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetCharts);
    return this.http.get<ApiResponse<number[]>>(apiUrl);
  }

  getSectionList(reportId: number): Observable<ApiResponse<ReportSectionData>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReportSections).replace(
      '{reportId}',
      reportId.toString()
    );
    return this.http.get<ApiResponse<ReportSectionData>>(apiUrl);
  }

  addNewSection(request: NewSectionRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.AddReportSection);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }
  updateSection(request: NewSectionRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateReportSections);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  deleteSection(request: NewSectionRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.DeleteReportSections);
    return this.http.delete<ApiResponse<boolean>>(apiUrl, { body: request });
  }

  addInitialReportData(request: AddReportRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.CreateReport);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  updateSectionValidationStatus(
    request: SectionValidationRequest
  ): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateValidationRequested);
    return this.http.patch<ApiResponse<boolean>>(apiUrl, request);
  }

  getReportGenerationStatus(reportId: number): Observable<ApiResponse<number>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.GenerateReportStatus
    ).replace('{reportId}', reportId.toString());
    return this.http.get<ApiResponse<number>>(apiUrl);
  }

  getReworkSectionList(
    request: SectionValidationRequest
  ): Observable<ApiResponse<ReportSectionData>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReworkSections);
    return this.http.post<ApiResponse<ReportSectionData>>(apiUrl, request);
  }

  downloadReport(fileId: string = '', filePath: string = '') {
    let apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ReportDownload);
  
    if (fileId) {
      apiUrl += `&fileId=${fileId}`;
    } else if (filePath) {
      apiUrl += `&filePath=${encodeURIComponent(filePath)}`;
    }
  
    return this.http.get(apiUrl, {
      responseType: 'blob',
      observe: 'response'
    });
  }
  
}
